/** App shell: navigation, language toggle, validation banner, disclaimer. */
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';

interface PatientResponse {
  patient: { name: string; nickname?: string | null } | null;
  validation: { isValidated: boolean; validatedByName: string | null };
}

interface IntakeResponse {
  submissions: { status: 'pending' | 'reviewed' | 'dismissed' }[];
}

const NAV: { to: string; key: string; label: { fr: string; en: string } }[] = [
  { to: '/', key: 'overview', label: { fr: 'Aperçu', en: 'Overview' } },
  { to: '/labs', key: 'labs', label: { fr: 'NFS / Labos', en: 'NFS / Labs' } },
  { to: '/serology', key: 'serology', label: { fr: 'Sérologie', en: 'Serology' } },
  { to: '/medications', key: 'meds', label: { fr: 'Médicaments', en: 'Medications' } },
  { to: '/dose-review', key: 'dose', label: { fr: 'Révision de dose', en: 'Dose review' } },
  { to: '/appointments', key: 'appts', label: { fr: 'RDV / Examens', en: 'Appointments' } },
  { to: '/adherence', key: 'adh', label: { fr: 'Observance', en: 'Adherence' } },
  { to: '/observations', key: 'obs', label: { fr: 'Observations / Questions', en: 'Observations / Questions' } },
  { to: '/reports-inbox', key: 'inbox', label: { fr: 'Comptes rendus à analyser', en: 'Reports inbox' } },
  { to: '/attachments', key: 'att', label: { fr: 'Pièces jointes', en: 'Attachments' } },
  { to: '/reports', key: 'rep', label: { fr: 'Rapports', en: 'Reports' } },
  { to: '/notes', key: 'notes', label: { fr: 'Notes cliniques', en: 'Clinical notes' } },
  { to: '/settings', key: 'settings', label: { fr: 'Paramètres', en: 'Settings' } },
  { to: '/admin', key: 'admin', label: { fr: 'Administration', en: 'Admin' } },
];

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { data } = useQuery({ queryKey: ['patient'], queryFn: () => api.get<PatientResponse>('/patient'), enabled: !!user });
  const { data: intake } = useQuery({ queryKey: ['intake'], queryFn: () => api.get<IntakeResponse>('/intake/submissions'), enabled: !!user });
  const pendingCount = intake?.submissions.filter((s) => s.status === 'pending').length ?? 0;

  const notValidated = data && !data.validation.isValidated;

  const navItems = (onNavigate?: () => void) =>
    NAV.map((n) => (
      <NavLink
        key={n.key}
        to={n.to}
        end={n.to === '/'}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm ${isActive ? 'bg-brand-light text-brand-dark font-medium' : 'text-slate-700 hover:bg-slate-100'}`
        }
      >
        <span>{n.key === 'notes' || n.key === 'settings' ? t(`nav.${n.key}`) : n.label[locale]}</span>
        {n.key === 'inbox' && pendingCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-brand text-white text-xs font-semibold">
            {pendingCount}
          </span>
        )}
      </NavLink>
    ));

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-brand-dark text-white px-4 py-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label={t('nav.openMenu')}
            className="md:hidden px-2 py-1 rounded bg-brand hover:bg-brand-dark border border-brand-light/30"
          >
            ☰
          </button>
          <span className="font-bold text-lg">ToxoFollow</span>
          {data?.patient && (
            <span className="text-brand-light text-sm truncate max-w-[40vw]">{data.patient.nickname || data.patient.name}</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm min-w-0">
          <button onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')} className="px-2 py-1 rounded bg-brand hover:bg-brand-dark border border-brand-light/30">
            {locale === 'fr' ? 'EN' : 'FR'}
          </button>
          <span className="truncate max-w-[40vw]">{user?.name}</span>
          <button onClick={() => { void logout().then(() => navigate('/login')); }} className="underline">
            {locale === 'fr' ? 'Déconnexion' : 'Logout'}
          </button>
        </div>
      </header>

      {notValidated && (
        <div className="bg-amber-100 text-amber-900 px-4 py-2 text-sm border-b border-amber-200">
          ⚠️ {t('banner.notValidated')}
        </div>
      )}

      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <nav className="hidden md:block w-56 bg-white border-r border-slate-200 p-3 space-y-1">
          {navItems()}
        </nav>

        {/* Mobile off-canvas drawer */}
        {mobileNavOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/30 z-30"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
        )}
        <nav
          className={`md:hidden fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg p-3 space-y-1 overflow-y-auto transform transition-transform duration-200 ${
            mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="font-bold text-brand-dark">ToxoFollow</span>
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              aria-label={t('nav.closeMenu')}
              className="px-2 py-1 rounded text-slate-500 hover:bg-slate-100"
            >
              ✕
            </button>
          </div>
          {navItems(() => setMobileNavOpen(false))}
        </nav>

        <main className="flex-1 p-4 md:p-6 max-w-6xl w-full">{children}</main>
      </div>

      <footer className="bg-slate-100 text-slate-500 text-xs px-4 py-2 border-t border-slate-200">
        {t('disclaimer.short')}
      </footer>
    </div>
  );
}
